import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch } from 'react-redux';
import PresignedAvatar from '../../../components/common/PresignedAvatar';
import { getOrCreateDirectMessage, fetchDirectMessages } from '../../../store/slices/channelSlice';
import { addNotification } from '../../../store/slices/uiSlice';
import { setCurrentChannel } from '../../../store/slices/chatSlice';
import { startDirectCall } from '../../../services/liveKitService';
import { openLiveKitCallWindow } from '../../../utils/liveKitCallWindow';
import { getLiveKitRoomName } from '../../../utils/meetingUtils';
import TaskCreationDialog from '../../../components/tasks/TaskCreationDialog';
import { TaskStatus } from '../../../store/slices/taskSlice';

export const initialsFromName = (name) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return (parts[0] || '?').slice(0, 2).toUpperCase();
};

const phoneDigits = (phone) => String(phone || '').replace(/\D/g, '');

const NeedToCallDetailModal = ({
  item,
  user,
  onClose,
  onCalled,
  onSkip,
  onFollowUp,
}) => {
  const dispatch = useDispatch();
  const [callingWorkloop, setCallingWorkloop] = useState(false);
  const [openingChat, setOpeningChat] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);

  if (!item || typeof document === 'undefined') return null;

  const label = item.name || 'Someone';
  const phone = String(item.phone || userPhoneFallback(user) || '').trim();
  const digits = phoneDigits(phone);
  const telHref = phone ? `tel:${phone.replace(/[^\d+]/g, '')}` : '';
  const whatsappHref = digits ? `https://wa.me/${digits}` : '';
  const canWorkloopCall = Boolean(item.targetUserId);
  const canOpenChat = Boolean(item.targetUserId || user?.id);

  const startPhoneCall = () => {
    if (!telHref) {
      dispatch(addNotification({ type: 'error', message: 'No phone number on this card' }));
      return;
    }
    window.open(telHref, '_self');
  };

  const startWhatsAppCall = () => {
    if (!whatsappHref) {
      dispatch(addNotification({ type: 'error', message: 'No phone number for WhatsApp' }));
      return;
    }
    window.open(whatsappHref, '_blank', 'noopener,noreferrer');
  };

  const startWorkloopCall = async () => {
    const peerId = item.targetUserId || user?.id;
    if (!peerId) {
      dispatch(addNotification({ type: 'error', message: 'Workloop call needs a Workloop teammate' }));
      return;
    }
    if (callingWorkloop) return;
    setCallingWorkloop(true);
    try {
      const channel = await dispatch(getOrCreateDirectMessage(peerId)).unwrap();
      const channelId = channel?.id || channel?._id;
      if (!channelId) throw new Error('Could not open a direct chat');
      const message = await startDirectCall(channelId, `Call ${label}`);
      const opened = openLiveKitCallWindow({
        channelId,
        messageId: message.id,
        roomName: getLiveKitRoomName(message.meetingInfo),
        title: message.meetingInfo?.label || `Call ${label}`,
      });
      if (!opened) {
        dispatch(addNotification({
          type: 'error',
          message: 'Could not open the call window. Try again.',
        }));
      }
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data || err?.message;
      dispatch(addNotification({
        type: 'error',
        message: typeof msg === 'string' ? msg.replace(/^Error:\s*/i, '') : 'Failed to start Workloop call',
      }));
    } finally {
      setCallingWorkloop(false);
    }
  };

  const openChat = async () => {
    const peerId = item.targetUserId || user?.id;
    if (!peerId) {
      dispatch(addNotification({ type: 'error', message: 'Chat is only available for a Workloop teammate' }));
      return;
    }
    if (openingChat) return;
    setOpeningChat(true);
    try {
      const channel = await dispatch(getOrCreateDirectMessage(peerId)).unwrap();
      await dispatch(fetchDirectMessages());
      if (!channel?.id) throw new Error('Could not open chat');
      dispatch(setCurrentChannel(channel));
      window.dispatchEvent(new CustomEvent('workloop:open-channel', { detail: { channel } }));
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data || err?.message;
      dispatch(addNotification({
        type: 'error',
        message: typeof msg === 'string' ? msg.replace(/^Error:\s*/i, '') : 'Failed to open chat',
      }));
    } finally {
      setOpeningChat(false);
    }
  };

  const escapeHtml = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  const taskTitle = `Call ${label}`;
  const taskDescription = [
    phone ? `<p>Number: ${escapeHtml(phone)}</p>` : '',
    item.reason ? `<p>Reason: ${escapeHtml(item.reason)}</p>` : '',
  ].filter(Boolean).join('');

  return ReactDOM.createPortal(
    <>
      <div
        className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/50"
        role="dialog"
        aria-modal="true"
        aria-labelledby="need-to-call-detail-title"
        onClick={onClose}
      >
        <div
          className="relative w-full max-w-md rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 shadow-2xl"
          onClick={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-800"
            aria-label="Close"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="flex items-start gap-3 pr-8">
            {user ? (
              <PresignedAvatar
                fileKey={user.profilePicture}
                cacheKey={user.username}
                name={label}
                initials={initialsFromName(label)}
                className="h-12 w-12 rounded-full object-cover flex-shrink-0"
                fallbackClassName="flex h-12 w-12 items-center justify-center rounded-full bg-sky-600 text-white text-sm font-bold flex-shrink-0"
              />
            ) : (
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-sky-600 text-white text-sm font-bold flex-shrink-0">
                {initialsFromName(label)}
              </span>
            )}
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-sky-700 dark:text-sky-300">
                Whom to call
              </p>
              <h3 id="need-to-call-detail-title" className="text-lg font-semibold text-gray-900 dark:text-white break-words">
                {label}
              </h3>
              {item.reason ? (
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap break-words">
                  {item.reason}
                </p>
              ) : null}
            </div>
          </div>

          <div className="mt-4">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Number
            </p>
            <div className="mt-1 flex items-center gap-2">
              <p className="min-w-0 flex-1 text-base font-semibold tabular-nums text-gray-900 dark:text-white truncate">
                {phone || 'No number'}
              </p>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  type="button"
                  onClick={startPhoneCall}
                  disabled={!telHref}
                  title="Phone"
                  aria-label="Phone"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={startWorkloopCall}
                  disabled={!canWorkloopCall || callingWorkloop}
                  title="Workloop"
                  aria-label="Workloop"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-sky-600 hover:bg-sky-700 disabled:opacity-40 overflow-hidden"
                >
                  {callingWorkloop ? (
                    <span className="h-3.5 w-3.5 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                  ) : (
                    <img
                      src={`${process.env.PUBLIC_URL || ''}/images/favicon.png`}
                      alt=""
                      className="h-5 w-5 object-contain"
                    />
                  )}
                </button>
                <button
                  type="button"
                  onClick={startWhatsAppCall}
                  disabled={!whatsappHref}
                  title="WhatsApp"
                  aria-label="WhatsApp"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full text-white bg-[#25D366] hover:bg-[#20bd5a] disabled:opacity-40"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={openChat}
                  disabled={!canOpenChat || openingChat}
                  title="Chat"
                  aria-label="Chat"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40"
                >
                  {openingChat ? (
                    <span className="h-3.5 w-3.5 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4.255-.947L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={onCalled}
              className="px-2 py-2 rounded-xl text-[11px] font-semibold text-emerald-800 bg-emerald-50 hover:bg-emerald-100 dark:text-emerald-200 dark:bg-emerald-950/50 dark:hover:bg-emerald-900/50"
            >
              Called, close
            </button>
            <button
              type="button"
              onClick={onSkip}
              className="px-2 py-2 rounded-xl text-[11px] font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700"
            >
              Skip
            </button>
            <button
              type="button"
              onClick={onFollowUp}
              className="px-2 py-2 rounded-xl text-[11px] font-semibold text-amber-800 bg-amber-50 hover:bg-amber-100 dark:text-amber-200 dark:bg-amber-950/50 dark:hover:bg-amber-900/50"
            >
              Follow-up
            </button>
          </div>

          <button
            type="button"
            onClick={() => setTaskOpen(true)}
            className="mt-4 w-full px-3 py-2 rounded-xl text-sm font-semibold text-white bg-violet-600 hover:bg-violet-700"
          >
            Create task
          </button>
        </div>
      </div>
      <TaskCreationDialog
        isOpen={taskOpen}
        onClose={() => setTaskOpen(false)}
        initialTitle={taskTitle}
        initialDescription={taskDescription}
        defaultStatus={TaskStatus.PICK_TODAY}
        initialDelegateId=""
        onSaved={() => {
          setTaskOpen(false);
          dispatch(addNotification({ type: 'success', message: 'Task created' }));
        }}
      />
    </>,
    document.body
  );
};

function userPhoneFallback(user) {
  return String(user?.mobileNumber || user?.contactNumber || '').trim();
}

export default NeedToCallDetailModal;
